import { ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { getLocalizedInternalUrl } from "@/lib/blog-utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

interface BlogArticle {
  title: string;
  excerpt: string;
  url: string;
  image: string;
  imageAlt?: string;
  category: string;
  readingTime: number;
}

const ArticlesSection = () => {
  const { t, language } = useLanguage();
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('fetch-blog-articles', {
          body: { language }
        });
        
        if (error) {
          console.error('Error fetching articles:', error);
          return;
        }

        if (data?.success && data?.articles) {
          setArticles(data.articles);
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [language]);

  const fallbackArticles: BlogArticle[] = [
    {
      title: t("articles.article1Title"),
      excerpt: t("articles.article1Excerpt"),
      category: t("articles.article1Category"),
      url: "/blog",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=400&fit=crop",
      readingTime: 5,
    },
    {
      title: t("articles.article2Title"),
      excerpt: t("articles.article2Excerpt"),
      category: t("articles.article2Category"),
      url: "/blog",
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=400&fit=crop",
      readingTime: 4,
    },
    {
      title: t("articles.article3Title"),
      excerpt: t("articles.article3Excerpt"),
      category: t("articles.article3Category"),
      url: "/blog",
      image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&h=400&fit=crop",
      readingTime: 6,
    },
  ];

  const displayArticles = articles.length > 0 ? articles : fallbackArticles;

  return (
    <section className="py-16 lg:py-24 px-2 bg-secondary/30">
      <div className="container mx-auto max-w-7xl">
        <div className="relative mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-cta text-center">
            {t("articles.title")}
          </h2>
          <div className="flex justify-center mt-6 sm:absolute sm:right-0 sm:top-1/2 sm:-translate-y-1/2 sm:mt-0">
            <Link to={language === 'en' ? '/en/blog' : '/blog'}>
              <Button variant="outline" className="group">
                {t("articles.viewAll")}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card border border-border/50 rounded-2xl overflow-hidden">
                <Skeleton className="h-48 w-full" />
                <div className="p-6 space-y-3">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {displayArticles.map((article, index) => (
                <CarouselItem key={index} className="pl-4 basis-full md:basis-1/2 lg:basis-1/3">
                  <article className="group bg-card border border-border/50 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 h-full">
                    <Link to={getLocalizedInternalUrl(article.url, language)} className="block h-full">
                      <div className="relative h-48 overflow-hidden bg-muted">
                        <img 
                          src={article.image}
                          alt={article.imageAlt || article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.src = "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=400&fit=crop";
                          }}
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                            {article.category}
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-6 flex flex-col h-[calc(100%-12rem)]">
                        <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        
                        <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                          {article.excerpt}
                        </p>
                        
                        <div className="mt-auto flex items-center justify-between">
                          {article.readingTime > 0 ? (
                            <div className="flex items-center gap-1 text-muted-foreground text-sm">
                              <Clock className="w-4 h-4" />
                              <span>{article.readingTime} min read</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-muted-foreground text-sm">
                              <Clock className="w-4 h-4" />
                              <span>{Math.max(1, Math.ceil((article.excerpt?.length || 0) / 1000))} min read</span>
                            </div>
                          )}
                          
                          <span className="text-primary font-medium text-sm flex items-center gap-1">
                            {t("articles.readMore")}
                            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </article>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-4 lg:-left-6" />
            <CarouselNext className="hidden md:flex -right-4 lg:-right-6" />
          </Carousel>
        )}
      </div>
    </section>
  );
};

export default ArticlesSection;
